import { useState, type ReactNode } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { useAuth } from '../auth/useAuth'
import { advancedAdminNavItems, dailyAdminNavItems } from './adminNavigation'
import { BrandLogo } from '../brand/BrandLogo'
import { isAdminFeatureEnabled } from '../../config/featureFlags'
import type { AdminNavItem } from './adminNavigation'

function AdminNavLink({ item, onClick, mobile = false }: { item: AdminNavItem; onClick?: () => void; mobile?: boolean }) {
  const enabled = isAdminFeatureEnabled(item.feature)
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      title={enabled ? item.description : `${item.label} is temporarily unavailable`}
      aria-disabled={!enabled}
      className={({ isActive }) => mobile
        ? `mobile-admin-menu-link ${isActive ? 'mobile-admin-menu-link-active' : ''}`
        : `app-nav-link ${isActive ? 'app-nav-link-active' : ''} ${enabled ? '' : 'app-nav-link-locked'}`}
    >
      <span>{item.label}</span>
      {!enabled && <span className="nav-lock-badge">Locked</span>}
    </NavLink>
  )
}

export function AdminShell({ children }: { children: ReactNode }) {
  const { logout } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const visibleDailyAdminNavItems = dailyAdminNavItems.filter((item) => isAdminFeatureEnabled(item.feature))
  const visibleAdvancedAdminNavItems = advancedAdminNavItems.filter((item) => isAdminFeatureEnabled(item.feature))
  const grouped = visibleDailyAdminNavItems.reduce<Record<string, typeof dailyAdminNavItems>>((groups, item) => {
    groups[item.group] = [...(groups[item.group] ?? []), item]
    return groups
  }, {})

  return <div className="app-shell admin-shell">
    <aside className="app-sidebar">
      <Link className="brand-lockup" to="/admin">
        <BrandLogo subtitle="School fee operations" />
      </Link>
      <nav className="app-nav" aria-label="Admin navigation">
        <NavLink end to="/admin" className={({ isActive }) => `app-nav-link ${isActive ? 'app-nav-link-active' : ''}`}>Dashboard</NavLink>
        {visibleDailyAdminNavItems.map((item) => <AdminNavLink key={item.to} item={item} />)}
        <div className="app-nav-section" aria-label="Settings / Advanced">
          <span className="app-nav-section-label">Settings / Advanced</span>
          {visibleAdvancedAdminNavItems.map((item) => <AdminNavLink key={item.to} item={item} />)}
        </div>
      </nav>
      <button className="button button-secondary app-logout" type="button" onClick={() => void logout()}>Log out</button>
    </aside>
    <div className="app-content">
      <div className="mobile-admin-bar">
        <Link className="brand-lockup" to="/admin">
          <BrandLogo compact />
        </Link>
        <button className="button button-small button-secondary" type="button" aria-expanded={menuOpen} aria-controls="mobile-admin-menu" onClick={() => setMenuOpen((value) => !value)}>Menu</button>
        <button className="button button-small button-secondary" type="button" onClick={() => void logout()}>Log out</button>
      </div>
      {menuOpen && <nav id="mobile-admin-menu" className="mobile-admin-menu" aria-label="Admin navigation">
        <NavLink end to="/admin" onClick={() => setMenuOpen(false)}>Dashboard</NavLink>
        {(['Primary', 'Daily work'] as const).map((group) => <div className="mobile-admin-menu-group" key={group}>
          <strong>{group}</strong>
          {(grouped[group] ?? []).map((item) => <AdminNavLink key={item.to} item={item} onClick={() => setMenuOpen(false)} mobile />)}
        </div>)}
        <div className="mobile-admin-menu-group">
          <strong>Settings / Advanced</strong>
          {visibleAdvancedAdminNavItems.map((item) => <AdminNavLink key={item.to} item={item} onClick={() => setMenuOpen(false)} mobile />)}
          <span className="mobile-menu-note">Repair and restore tools</span>
        </div>
      </nav>}
      {children}
    </div>
  </div>
}
